import type {ImageCrop, RequirementOf} from '../../lib/types';
import {ReportImage} from '../ReportImage';
import {cropTransform} from '../../lib/crop';
import {resolveElementSize} from '../../lib/element-size';
import {effectivePositionMode} from '../../lib/position-mode';
import {PhotoCropper} from '../PhotoCropper';
import {type ElemProps} from './element-props';
import {buildBaseStyle} from './element-styles';

export function LogoRenderer({
  item,
  cropEditId,
  zoom,
  onCropApply,
  onCropCancel,
}: ElemProps & {
  item: RequirementOf<'logo'>;
  cropEditId?: string | null;
  zoom?: number;
  onCropApply?: (crop: ImageCrop) => void;
  onCropCancel?: () => void;
}) {
  const s = item.style;
  const style = buildBaseStyle(s);
  const lCfg = item.config;
  const isAbsolute = effectivePositionMode(item) === 'absolute';
  const justifyMap: Record<string, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  const justify = justifyMap[lCfg.align ?? 'left'] ?? 'flex-start';

  // Crop frame size in px (the element box for absolute, the config size for flow),
  // resolved by the same helper the toolbar's W/H inputs read.
  const {width: frameW, height: frameH} = resolveElementSize(item);

  // Active crop editing — render the interactive cropper in place.
  if (lCfg.logo_url && cropEditId === item.id) {
    return (
      <div
        style={{
          ...style,
          width: isAbsolute ? '100%' : frameW,
          height: isAbsolute ? '100%' : frameH,
        }}
      >
        <PhotoCropper
          src={lCfg.logo_url}
          frameW={frameW}
          frameH={frameH}
          zoom={zoom ?? 1}
          initial={lCfg.crop ?? {scale: 1, offset_x: 0, offset_y: 0}}
          onApply={(c) => onCropApply?.(c)}
          onCancel={() => onCropCancel?.()}
        />
      </div>
    );
  }

  // Saved crop — fixed frame, image covers and is panned/zoomed (no distortion).
  if (lCfg.logo_url && lCfg.crop) {
    return (
      <div
        style={{
          ...style,
          width: isAbsolute ? '100%' : frameW,
          height: isAbsolute ? '100%' : frameH,
          overflow: 'hidden',
        }}
        className="rounded"
      >
        <ReportImage
          src={lCfg.logo_url}
          alt="Logo"
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: cropTransform(lCfg.crop),
            transformOrigin: 'center',
          }}
        />
      </div>
    );
  }

  if (lCfg.logo_url) {
    return (
      <div
        style={{
          ...style,
          display: 'flex',
          justifyContent: isAbsolute ? 'center' : justify,
          width: '100%',
          height: isAbsolute ? '100%' : undefined,
        }}
      >
        <ReportImage
          src={lCfg.logo_url}
          alt="Logo"
          /**
           * The RESOLVED size, so the box is the same whether the image arrives
           * or not.
           *
           * Unset used to mean the image's intrinsic size, which makes a
           * document's LENGTH depend on a network result: a logo that loads
           * takes its natural height, one that fails collapses to its alt text,
           * and the pagination follows. That is how the same JAC fitted a row
           * more on the screen whose letterhead was broken — and it made the job
           * and the client's copy end on different rows for a reason neither
           * page could show.
           *
           * `resolveElementSize` is the same answer the toolbar's W/H fields
           * report for an unsized logo, so the canvas now agrees with what the
           * author is told the size is — and it reads `config.width` first, so
           * an authored size is already what `frameW` holds. `object-fit` still
           * letterboxes the image inside the box, so nothing is distorted to
           * fit.
           */
          style={{
            width: isAbsolute ? '100%' : frameW,
            height: isAbsolute ? '100%' : frameH,
            objectFit: (lCfg.object_fit ?? 'contain') as React.CSSProperties['objectFit'],
            maxWidth: '100%',
          }}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        ...style,
        width: isAbsolute ? '100%' : (lCfg.width ?? 96),
        height: isAbsolute ? '100%' : (lCfg.height ?? 48),
      }}
      className="flex items-center justify-center border border-dashed border-gray-300 bg-gray-50 rounded text-gray-300 text-[10px]"
    >
      Photo
    </div>
  );
}
